import type { JSX } from 'react';
import { Button } from '@heroui/react';
import { Bookmark, Boxes, FolderKanban, GitBranch } from 'lucide-react';

import { formatTimeAgo } from '~/utils/formatTimestamp';

import type { Product } from '~/types';

interface BookmarkToggleProps {
  className?: string;
  isPinned: boolean;
  onToggle: () => void;
  productName: string;
}

interface CountBadgeProps {
  count: number;
  icon: JSX.Element;
  label: string;
  tone: 'primary' | 'success';
}

export interface ProductCardProps {
  /** Whether this product is pinned to the top of the grid — see useProductFiltering. */
  isPinned: boolean;
  /** Callback fired with the product's id when the card is activated. */
  onSelect: (id: string) => void;
  /** Callback fired with the product's id when the bookmark toggle is clicked. */
  onTogglePin: (id: string) => void;
  /** Product to display. Props-only — this component never fetches data. */
  product: Product;
  /** Layout mode — see uiStore's `productsViewMode`. */
  viewMode: 'grid' | 'list';
}

/**
 * Bookmark/pin toggle, rendered as a sibling of the card's main-content
 * button (never nested inside it — two interactive controls cannot nest).
 * Mirrors ProductGrid's grid/list view-mode buttons: same HeroUI Button
 * config (isIconOnly, size sm, variant light), same aria-pressed pattern.
 * Pattern: docs/design-patterns/frontend/accessibility.md (semantic-html-first, keyboard-operable)
 * Pattern: docs/design-patterns/frontend/visual-polish-and-motion.md (icon-consistency)
 */
function BookmarkToggle({
  className,
  isPinned,
  onToggle,
  productName,
}: BookmarkToggleProps): JSX.Element {
  return (
    <Button
      isIconOnly
      aria-label={`${isPinned ? 'Unpin' : 'Pin'} ${productName}`}
      aria-pressed={isPinned}
      className={className}
      size="sm"
      variant="light"
      onPress={onToggle}
    >
      <Bookmark
        aria-hidden="true"
        className={`w-4 h-4 ${isPinned ? 'fill-current text-primary' : ''}`}
      />
    </Button>
  );
}

/**
 * Always-visible count badge (repo/project counts render even at zero, so the
 * card layout stays stable whether or not a product has repos/projects yet).
 *
 * Uses the light-tint-background / dark-scale-text pairing (`bg-{tone}-100
 * dark:bg-{tone}-200 text-{tone}-700 dark:text-{tone}-800`) that
 * ProjectCard's status badge already uses for small (12px) badge text — not
 * the tinted `bg-{tone}/10 text-{tone}` combo used elsewhere in Products, nor
 * the solid `bg-{tone} text-{tone}-foreground` pairing used for larger pill
 * text. axe's color-contrast check measured the
 * `/10`-opacity combo at 2.87:1 and the solid pairing at 4.36:1 — both under
 * the WCAG AA 4.5:1 minimum required at this font size; the numbered-scale
 * pairing is the only one of the three that clears it.
 * Pattern: docs/design-patterns/frontend/accessibility.md (contrast-and-color)
 */
function CountBadge({ count, icon, label, tone }: CountBadgeProps): JSX.Element {
  const toneClasses =
    tone === 'primary'
      ? 'bg-primary-100 dark:bg-primary-200 text-primary-700 dark:text-primary-800'
      : 'bg-success-100 dark:bg-success-200 text-success-700 dark:text-success-800';

  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium shrink-0 ${toneClasses}`}
    >
      {icon}
      {count} {label}
      {count !== 1 ? 's' : ''}
    </span>
  );
}

/**
 * Display card for a single product on the /products grid.
 *
 * Pattern: docs/design-patterns/frontend/component-patterns.md (display-components-props-only)
 * Pattern: docs/design-patterns/frontend/accessibility.md (semantic-html-first)
 */
export function ProductCard({
  isPinned,
  onSelect,
  onTogglePin,
  product,
  viewMode,
}: ProductCardProps): JSX.Element {
  const repositoryCount = product.repositoryCount ?? 0;
  const projectCount = product.projectCount ?? 0;

  const badges = (
    <div className="flex items-center gap-1.5 shrink-0">
      <CountBadge
        count={repositoryCount}
        icon={<GitBranch className="w-3 h-3" />}
        label="repo"
        tone="primary"
      />
      <CountBadge
        count={projectCount}
        icon={<FolderKanban className="w-3 h-3" />}
        label="project"
        tone="success"
      />
    </div>
  );

  const handleClick = (): void => onSelect(product.id);
  const handleTogglePin = (): void => onTogglePin(product.id);
  const lastUpdated = (
    <span className="text-xs text-muted shrink-0">{formatTimeAgo(product.lastActivityAt)}</span>
  );

  if (viewMode === 'list') {
    return (
      <div className="relative w-full flex items-center gap-3 px-4 py-3 rounded-lg border border-default bg-surface hover:bg-surface-elevated transition-colors">
        <button
          type="button"
          onClick={handleClick}
          aria-label={`Open product ${product.name}`}
          className="flex-1 min-w-0 flex items-center gap-3 text-left rounded focus:outline-none focus:ring-2 focus:ring-primary/50"
        >
          <Boxes className="w-5 h-5 text-muted shrink-0" aria-hidden="true" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground truncate">{product.name}</p>
            {product.description && (
              <p className="text-xs text-muted truncate">{product.description}</p>
            )}
          </div>
          {badges}
        </button>
        {lastUpdated}
        <BookmarkToggle isPinned={isPinned} productName={product.name} onToggle={handleTogglePin} />
      </div>
    );
  }

  return (
    <div className="relative h-full">
      <button
        type="button"
        onClick={handleClick}
        aria-label={`Open product ${product.name}`}
        className="w-full h-full flex flex-col items-start gap-3 p-4 text-left rounded-lg border border-default bg-surface hover:bg-surface-elevated transition-colors focus:outline-none focus:ring-2 focus:ring-primary/50"
      >
        <Boxes className="w-6 h-6 text-muted shrink-0" aria-hidden="true" />
        <div className="min-w-0 w-full">
          <p className="text-sm font-medium text-foreground truncate">{product.name}</p>
          {product.description && (
            <p className="text-xs text-muted line-clamp-2 mt-0.5">{product.description}</p>
          )}
        </div>
        <div className="flex items-center justify-between gap-1.5 w-full">
          {badges}
          {lastUpdated}
        </div>
      </button>
      <BookmarkToggle
        className="absolute top-2 right-2"
        isPinned={isPinned}
        productName={product.name}
        onToggle={handleTogglePin}
      />
    </div>
  );
}
